import { useCallback } from "react";
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  WikiBacklinkDTO,
  WikiCommitDTO,
  WikiCommitViewDTO,
  WikiDiffLine,
  WikiLogEntryDTO,
  WikiPageDTO,
  WikiPageSummaryDTO,
  WikiPurgeHitDTO,
  WikiSpaceDTO,
  WikiTemplateDTO,
  WikiTitleDTO,
} from "@justone/shared";
import { http } from "./http";

export const wikiKeys = {
  spaces: ["wiki-spaces"] as const,
  space: (key: "company" | number) => ["wiki-space", key] as const,
  tree: (spaceId: number) => ["wiki-tree", spaceId] as const,
  deleted: (spaceId: number) => ["wiki-deleted", spaceId] as const,
  page: (id: number) => ["wiki-page", id] as const,
  history: (id: number) => ["wiki-history", id] as const,
  commit: (id: number) => ["wiki-commit", id] as const,
  diff: (id: number, against: number | null) => ["wiki-diff", id, against] as const,
  log: (spaceId: number, filter: LogFilter) => ["wiki-log", spaceId, filter] as const,
  backlinks: (id: number) => ["wiki-backlinks", id] as const,
  titles: ["wiki-titles"] as const,
  templates: ["wiki-templates"] as const,
};

export interface LogFilter {
  pageId?: number;
  userId?: number;
  kind?: string;
}

/* ───────────── 조회 ───────────── */

export const useWikiSpaces = () => useQuery({ queryKey: wikiKeys.spaces, queryFn: () => http.get<WikiSpaceDTO[]>("/api/wiki/spaces"), staleTime: Infinity });

/** 공통 위키 또는 채널 위키 (채널 위키는 처음 열 때 만들어진다) */
export function useWikiSpace(channelId: number | null) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: wikiKeys.space(channelId ?? "company"),
    queryFn: async () => {
      const s = await http.get<WikiSpaceDTO>(channelId === null ? "/api/wiki/company" : `/api/channels/${channelId}/wiki`);
      qc.setQueryData<WikiSpaceDTO[]>(wikiKeys.spaces, (list) => (list && !list.some((x) => x.id === s.id) ? [...list, s] : list));
      return s;
    },
    staleTime: Infinity,
  });
}

export const useWikiTree = (spaceId: number | undefined) =>
  useQuery({ queryKey: wikiKeys.tree(spaceId ?? 0), queryFn: () => http.get<WikiPageSummaryDTO[]>(`/api/wiki/spaces/${spaceId}/pages`), enabled: !!spaceId, staleTime: Infinity });

export const useWikiDeleted = (spaceId: number) => useQuery({ queryKey: wikiKeys.deleted(spaceId), queryFn: () => http.get<WikiPageSummaryDTO[]>(`/api/wiki/spaces/${spaceId}/deleted`) });

const wikiPageQuery = (id: number) => ({ queryKey: wikiKeys.page(id), queryFn: () => http.get<WikiPageDTO>(`/api/wiki/pages/${id}`), staleTime: 5_000 });

export const useWikiPage = (id: number) => useQuery(wikiPageQuery(id));

/** 링크에 마우스를 올리거나 포커스하면 페이지 정보를 미리 받는다 (누르면 저장된 내용이 바로 보이게) */
export function usePrefetchWikiPage() {
  const qc = useQueryClient();
  return useCallback((id: number) => void qc.prefetchQuery(wikiPageQuery(id)), [qc]);
}

export const useWikiHistory = (id: number, enabled = true) => useQuery({ queryKey: wikiKeys.history(id), queryFn: () => http.get<WikiCommitDTO[]>(`/api/wiki/pages/${id}/history`), enabled });

export const useWikiCommit = (id: number | null) =>
  useQuery({ queryKey: wikiKeys.commit(id ?? 0), queryFn: () => http.get<WikiCommitViewDTO>(`/api/wiki/commits/${id}`), enabled: id !== null, staleTime: Infinity });

export const useWikiDiff = (id: number | null, against: number | null) =>
  useQuery({
    queryKey: wikiKeys.diff(id ?? 0, against),
    queryFn: () => http.get<{ against: WikiCommitDTO | null; lines: WikiDiffLine[] }>(`/api/wiki/commits/${id}/diff${against ? `?against=${against}` : ""}`),
    enabled: id !== null,
    staleTime: Infinity,
    retry: false,
  });

export function useWikiLog(spaceId: number, filter: LogFilter) {
  return useQuery({
    queryKey: wikiKeys.log(spaceId, filter),
    queryFn: () => {
      const p = new URLSearchParams();
      if (filter.pageId) p.set("pageId", String(filter.pageId));
      if (filter.userId) p.set("userId", String(filter.userId));
      if (filter.kind) p.set("kind", filter.kind);
      return http.get<WikiLogEntryDTO[]>(`/api/wiki/spaces/${spaceId}/log?${p}`);
    },
  });
}

export const useBacklinks = (id: number) => useQuery({ queryKey: wikiKeys.backlinks(id), queryFn: () => http.get<WikiBacklinkDTO[]>(`/api/wiki/pages/${id}/backlinks`) });

export const useWikiTemplates = () => useQuery({ queryKey: wikiKeys.templates, queryFn: () => http.get<WikiTemplateDTO[]>("/api/wiki/templates"), staleTime: 5 * 60_000 });

export type TemplateBody = { name: string; description: string; title: string; markdown: string };

/** 관리자: 템플릿 만들기·고치기·지우기 */
export function useTemplateMutations() {
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: wikiKeys.templates });
  return {
    create: useMutation({ mutationFn: (body: TemplateBody) => http.post<WikiTemplateDTO>("/api/wiki/templates", body), onSuccess: refresh }),
    update: useMutation({ mutationFn: ({ id, ...body }: TemplateBody & { id: number }) => http.put<WikiTemplateDTO>(`/api/wiki/templates/${id}`, body), onSuccess: refresh }),
    remove: useMutation({ mutationFn: (id: number) => http.delete(`/api/wiki/templates/${id}`), onSuccess: refresh }),
  };
}

/** 스레드를 위키 초안으로 */
export function useThreadToWiki() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, title }: { messageId: number; title: string }) => http.post<WikiPageSummaryDTO>(`/api/messages/${messageId}/wiki`, { title }),
    onSuccess: (p) => upsertWikiPage(qc, p),
  });
}

export const useWikiTitles = () => useQuery({ queryKey: wikiKeys.titles, queryFn: () => http.get<WikiTitleDTO[]>("/api/wiki/titles"), staleTime: 60_000 });

export const fetchPurgeHits = (q: string) => http.get<{ hits: WikiPurgeHitDTO[]; current: { id: number; title: string }[] }>(`/api/admin/wiki/purge?q=${encodeURIComponent(q)}`);

/* ───────────── 캐시 (실시간 이벤트) ───────────── */

/** 트리에 있어야 하는지: 지워지지 않은 페이지 (초안은 서버가 작성자에게만 보낸다) */
export function upsertWikiPage(qc: QueryClient, page: WikiPageSummaryDTO) {
  for (const [key, list] of qc.getQueriesData<WikiPageSummaryDTO[]>({ queryKey: ["wiki-tree"] })) {
    if (!list) continue;
    const spaceId = key[1] as number;
    const without = list.filter((p) => p.id !== page.id);
    qc.setQueryData(key, spaceId === page.spaceId && !page.deleted ? [...without, page] : without);
  }
  void qc.invalidateQueries({ queryKey: wikiKeys.page(page.id) });
  void qc.invalidateQueries({ queryKey: wikiKeys.titles });
  void qc.invalidateQueries({ queryKey: ["wiki-deleted"] });
  void qc.invalidateQueries({ queryKey: ["wiki-backlinks"] });
}

export function onWikiCommit(qc: QueryClient, pageId: number, spaceId: number) {
  void qc.invalidateQueries({ queryKey: wikiKeys.history(pageId) });
  void qc.invalidateQueries({ queryKey: ["wiki-log", spaceId] });
  void qc.invalidateQueries({ queryKey: wikiKeys.page(pageId) });
}

/* ───────────── 변경 ───────────── */

export function useWikiMutations() {
  const qc = useQueryClient();
  const touch = (p: WikiPageSummaryDTO) => upsertWikiPage(qc, p);
  return {
    create: useMutation({
      mutationFn: ({ spaceId, ...body }: { spaceId: number; title: string; parentId?: number | null; markdown?: string }) => http.post<WikiPageSummaryDTO>(`/api/wiki/spaces/${spaceId}/pages`, body),
      onSuccess: touch,
    }),
    rename: useMutation({ mutationFn: ({ id, title }: { id: number; title: string }) => http.patch<WikiPageSummaryDTO>(`/api/wiki/pages/${id}`, { title }), onSuccess: touch }),
    move: useMutation({
      mutationFn: ({ id, ...body }: { id: number; parentId: number | null; position: number; spaceId?: number }) => http.post<WikiPageSummaryDTO>(`/api/wiki/pages/${id}/move`, body),
      onSuccess: touch,
    }),
    publish: useMutation({ mutationFn: ({ id, announce }: { id: number; announce: boolean }) => http.post<WikiPageSummaryDTO>(`/api/wiki/pages/${id}/publish`, { announce }), onSuccess: touch }),
    commit: useMutation({
      mutationFn: ({ id, message }: { id: number; message?: string }) => http.post<{ commit: WikiCommitDTO | null }>(`/api/wiki/pages/${id}/commit`, { message }),
      onSuccess: (_r, { id }) => void qc.invalidateQueries({ queryKey: wikiKeys.page(id) }),
    }),
    remove: useMutation({ mutationFn: (id: number) => http.delete<{ count: number }>(`/api/wiki/pages/${id}`) }),
    undelete: useMutation({ mutationFn: ({ id, withChildren }: { id: number; withChildren: boolean }) => http.post<{ count: number }>(`/api/wiki/pages/${id}/undelete`, { withChildren }) }),
    restore: useMutation({ mutationFn: (commitId: number) => http.post<{ commit: WikiCommitDTO | null }>(`/api/wiki/commits/${commitId}/restore`) }),
    revert: useMutation({ mutationFn: ({ commitId, withChildren }: { commitId: number; withChildren?: boolean }) => http.post<WikiPageSummaryDTO>(`/api/wiki/commits/${commitId}/revert`, { withChildren }), onSuccess: touch }),
    lock: useMutation({
      mutationFn: ({ id, locked }: { id: number; locked: boolean }) => http.post<WikiPageSummaryDTO>(`/api/wiki/pages/${id}/lock`, { locked }),
      onSuccess: (p) => {
        touch(p);
        void qc.invalidateQueries({ queryKey: wikiKeys.page(p.id) });
      },
    }),
    watch: useMutation({
      mutationFn: ({ id, on }: { id: number; on: boolean }) => (on ? http.put : http.delete)<{ watching: boolean }>(`/api/wiki/pages/${id}/watch`),
      onSuccess: (r, { id }) => qc.setQueryData<WikiPageDTO>(wikiKeys.page(id), (p) => (p ? { ...p, watching: r.watching } : p)),
    }),
  };
}
